import { z } from 'zod';
import { PCMP_DIVISIONS, type PcmpDivision } from './pcmpQueue.types';

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const positiveInt = z.number().int().positive().optional().catch(undefined);

export const PCMP_PENDING_STAGES = [
  'MKT_PENDING',
  'MIS_PENDING',
  'ACC_PENDING',
  'MKTDISB_PENDING',
  'DISBURSED',
] as const;
export type PcmpPendingStage = (typeof PCMP_PENDING_STAGES)[number];

/** The filters every PCMP report takes. */
export interface PcmpReportFilters {
  projectId?: number | undefined;
  division?: PcmpDivision | undefined;
  departmentId?: number | undefined;
  dateFrom?: string | undefined;
  dateTo?: string | undefined;
}

/** The URL search fields of a report's filters. */
export const pcmpReportSearchShape = {
  projectId: positiveInt,
  division: z.enum(PCMP_DIVISIONS).optional().catch(undefined),
  departmentId: positiveInt,
  dateFrom: z.string().regex(DATE_ONLY_RE).optional().catch(undefined),
  dateTo: z.string().regex(DATE_ONLY_RE).optional().catch(undefined),
};

export const pcmpReportSearchSchema = z.object(pcmpReportSearchShape);
export type PcmpReportSearch = z.infer<typeof pcmpReportSearchSchema>;

export const pcmpPendingReportSearchSchema = z.object({
  ...pcmpReportSearchShape,
  stage: z.enum(PCMP_PENDING_STAGES).optional().catch(undefined),
});
export type PcmpPendingReportSearch = z.infer<typeof pcmpPendingReportSearchSchema>;

export interface PcmpPendingReportParams extends PcmpReportFilters {
  stage?: PcmpPendingStage | undefined;
}

export const pcmpReportFilterOptionsSchema = z.object({
  divisions: z.array(z.object({ code: z.enum(PCMP_DIVISIONS), label: z.string() })),
  departments: z.array(
    z.object({
      id: z.coerce.number(),
      name: z.string(),
      division: z.enum(PCMP_DIVISIONS).nullable(),
    }),
  ),
});

export type PcmpReportFilterOptions = z.infer<typeof pcmpReportFilterOptionsSchema>;

const pendingCellSchema = z.object({ qty: z.number(), amount: z.number() });

const pendingRowSchema = z.object({
  departmentId: z.number().nullable(),
  region: z.string(),
  cells: z.record(z.string(), pendingCellSchema),
});

export type PcmpPendingRow = z.infer<typeof pendingRowSchema>;

export const pcmpPendingReportSchema = z.object({
  cards: z.object({
    submitted: z.number(),
    pending: z.number(),
    verified: z.number(),
    disbursed: z.number(),
    awaitingPublish: z.number(),
  }),
  totalAmount: z.number(),
  columns: z.array(
    z.object({ key: z.string(), label: z.string(), lane: z.string(), laneLabel: z.string() }),
  ),
  divisions: z.array(
    z.object({
      division: z.enum(PCMP_DIVISIONS).nullable(),
      label: z.string(),
      rows: z.array(pendingRowSchema),
      subtotal: pendingRowSchema,
    }),
  ),
  grandTotal: pendingRowSchema,
  projectLabel: z.string(),
  reportDate: z.string(),
});

export type PcmpPendingReport = z.infer<typeof pcmpPendingReportSchema>;

/** One region, division subtotal or the grand total of the Performance report. */
const performanceLineSchema = z.object({
  name: z.string(),
  departmentId: z.number().nullable(),
  instituteCount: z.number(),
  salesTarget: z.number(),
  cmpAmount: z.number(),
  /** Paid out, net of returns. */
  disbursedAmount: z.number(),
  /** Proposed, not paid out yet on either channel, not rejected or returned (LDS-804/807). */
  processingAmount: z.number(),
  /** CMP amount - disbursed - processing. */
  due: z.number(),
  cmpPercent: z.number(),
  // Not sourced by the API yet: always null for now.
  netChallan: z.number().nullable(),
  variance: z.number().nullable(),
  duePercent: z.number().nullable(),
});

export type PcmpPerformanceLine = z.infer<typeof performanceLineSchema>;

const performanceDivisionSchema = z.object({
  division: z.enum(PCMP_DIVISIONS).nullable(),
  label: z.string(),
  color: z.string(),
  regions: z.array(performanceLineSchema),
  subtotal: performanceLineSchema,
});

export type PcmpPerformanceDivision = z.infer<typeof performanceDivisionSchema>;

export const pcmpPerformanceReportSchema = z.object({
  divisions: z.array(performanceDivisionSchema),
  grandTotal: performanceLineSchema,
  lowest: performanceDivisionSchema.nullable(),
  highest: performanceDivisionSchema.nullable(),
  periodLabel: z.string(),
  reportDate: z.string(),
});

export type PcmpPerformanceReport = z.infer<typeof pcmpPerformanceReportSchema>;
